import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGlobalLoading } from '../context/GlobalLoadingContext';
import UnifiedLoadingScreen from '../components/loading/UnifiedLoadingScreen';
import { Loader2, ArrowRight, Mail, Lock, ChevronRight, Calendar, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Custom CSS for the breakpoint
const customStyles = `
  @media (min-width: 1970px) and (min-height: 1080px) {
    .show-on-large {
      display: block !important;
    }
    .hide-on-large {
      display: none !important;
    }
  }
`;

const validatePassword = (password: string, t: (key: string) => string): { isValid: boolean; error: string | null } => {
  if (password.length < 8) {
    return { isValid: false, error: t('profile.passwordMinLength') };
  }
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, error: t('profile.passwordMustContainUppercase') };
  }
  if (!/[a-z]/.test(password)) {
    return { isValid: false, error: t('profile.passwordMustContainLowercase') };
  }
  if (!/\d/.test(password)) {
    return { isValid: false, error: t('profile.passwordMustContainNumber') };
  }
  return { isValid: true, error: null };
};

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { signIn, signUp, user, loading, error } = useAuth();
  const { state: globalLoadingState, startLoginFlow } = useGlobalLoading();

  // Warm up Cloud Function as soon as login page loads to eliminate cold start
  useEffect(() => {
    const warmCloudFunction = async () => {
      try {
        const proxyUrl = import.meta.env.VITE_AIRTABLE_PROXY_URL;
        if (proxyUrl) {
          await fetch(`${proxyUrl}/warm`, { 
            method: 'HEAD',
            mode: 'cors'
          });
          console.log('Cloud Function warmed up successfully');
        }
      } catch (error) {
        // Silent fail is OK - warming is an optimization
        console.log('Cloud Function warming failed (non-critical):', error);
      }
    };
    
    warmCloudFunction();
  }, []);

  // Auth success callback - triggers unified loading instead of direct navigation
  const handleAuthSuccess = () => {
    console.log('🎯 Login successful - starting unified loading flow');
    startLoginFlow();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    // Clear all errors before validation
    setLocalError(null);
    setIsSubmitting(true);
    
    try {
      if (isSignUp) {
        const { isValid, error: validationError } = validatePassword(password, t);
        if (!isValid) {
          setLocalError(validationError);
          setIsSubmitting(false);
          return;
        }

        if (password !== confirmPassword) {
          setLocalError(t('profile.passwordMismatch'));
          setIsSubmitting(false);
          return;
        }

        await signUp(email, password, handleAuthSuccess);
      } else {
        await signIn(email, password, handleAuthSuccess);
      }
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clear errors when switching between login and signup
  const handleModeSwitch = () => {
    setIsSignUp(!isSignUp);
    setLocalError(null);
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  // Load Google Fonts and custom styles
  useEffect(() => {
    const link1 = document.createElement('link');
    link1.rel = 'stylesheet';
    link1.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap';
    document.head.appendChild(link1);
    
    const link2 = document.createElement('link');
    link2.rel = 'stylesheet';
    link2.href = 'https://fonts.googleapis.com/css2?family=Marcellus&display=swap';
    document.head.appendChild(link2);
    
    // Add custom styles for breakpoints
    const styleElement = document.createElement('style');
    styleElement.innerHTML = customStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(link1);
      document.head.removeChild(link2);
      document.head.removeChild(styleElement);
    };
  }, []);

  // Show unified loading after successful auth during critical loading
  // The critical stages now include high-priority conversations, so the loading screen
  // will stay visible until the recent meetings and their conversations are loaded
  if (user && globalLoadingState.isCriticalLoading) {
    return <UnifiedLoadingScreen />;
  }

  // Show simple loader for initial auth loading (but not during form submission)
  if (loading && !isSubmitting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2e35]" />
      </div>
    );
  }

  // Navigate to main page once user is authenticated and critical loading is complete
  // This happens after high-priority conversations are loaded, preventing the double loading issue
  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex bg-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      {/* Left Side - Login */}
      <div className="w-full md:w-1/2 flex flex-col relative min-h-screen pt-0 pb-4 px-4 sm:px-6">
        {/* Logo positioned above the welcome text */}
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md">
            <div className="flex justify-center mb-8">
              <img 
                src="/assets/images/sol-media-logo-black.png"
                alt="Sol Media Logo"
                className="h-9" 
              />
            </div>
            
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-2xl font-bold text-gray-800" style={{ fontFamily: "'Marcellus', serif" }}>
                {isSignUp ? t('auth.createAccount') : t('auth.welcomeBack')}
              </h2>
              <p className="text-gray-500 mt-2 text-sm">
                {isSignUp ? t('auth.createAccountDescription') : t('auth.accessDescription')}
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              {(error || localError) && (
                <div className="bg-red-100 border-l-4 border-red-400 text-red-700 px-4 py-3 text-sm">
                  {(localError || error)?.split('\n').map((line, i) => (
                    <div key={i} className="mb-1 last:mb-0">
                      {line}
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-800">
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="block w-full pl-10 pr-4 py-3 border-2 border-gray-200 shadow-sm focus:outline-none focus:ring-0 focus:border-[#1a2e35] transition-colors text-sm placeholder:text-xs"
                    placeholder={t('auth.emailPlaceholder')}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-800">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="block w-full pl-10 pr-10 py-3 border-2 border-gray-200 shadow-sm focus:outline-none focus:ring-0 focus:border-[#1a2e35] transition-colors text-sm placeholder:text-xs"
                    placeholder="••••••••"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              {isSignUp && (
                <div className="space-y-2">
                  <div className="text-xs text-gray-500 p-3 bg-gray-50 border border-gray-100">
                    <p className="font-medium mb-1">La contraseña debe contener al menos:</p>
                    <ul className="list-disc ml-4 space-y-0.5">
                      <li>8 caracteres</li>
                      <li>Una letra mayúscula y una minúscula</li>
                      <li>Un número</li>
                    </ul>
                  </div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-800">
                    Confirmar Contraseña
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="block w-full pl-10 pr-10 py-3 border-2 border-gray-200 shadow-sm focus:outline-none focus:ring-0 focus:border-[#1a2e35] transition-colors text-sm placeholder:text-xs"
                      placeholder="••••••••"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none"
                        aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="pt-1">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center items-center py-3 px-6 border border-transparent shadow-md text-sm font-medium text-white bg-[#1a2e35] hover:bg-[#152229] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700 transition-all transform hover:translate-y-[1px]"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      <span>{isSignUp ? 'Creando cuenta...' : 'Iniciando sesión...'}</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between w-full">
                      <span>{isSignUp ? 'Crear cuenta' : 'Iniciar sesión'}</span>
                      <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </div>
                  )}
                </button>
              </div>
              
              <div>
                <div className="border-t border-gray-100 my-2"></div>
                <div className="text-center pt-1">
                  <button
                    type="button"
                    onClick={handleModeSwitch}
                    className="text-sm text-[#1a2e35] hover:text-[#152229] transition-colors font-medium"
                  >
                    {isSignUp ? '¿Ya tienes cuenta? Inicia sesión' : '¿No tienes cuenta? Regístrate'}
                  </button>
                </div>
              </div>
            </form>

            <div className="mt-6 text-center text-xs text-gray-400">
              <p>© {new Date().getFullYear()} SOL MEDIA SOLUTIONS</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Marketing Preview */}
      <div className="hidden md:block w-1/2 bg-gray-50 relative min-h-screen overflow-hidden">
        {/* Image version for smaller screens */}
        <div className="hide-on-large block w-full h-full">
          <div className="p-10 flex flex-col justify-center h-full">
            <div className="mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Marcellus', serif" }}>
                Potencia tu adquisición<br />de clientes
              </h1>
              <p className="text-gray-600 max-w-md text-lg mb-6">
                Cada oportunidad comercial, meticulosamente gestionada en un solo lugar.
              </p>
              
              <div className="flex items-center">
                <button className="text-sm text-[#1a2e35] font-medium flex items-center">
                  <span>Accede para gestionar tus leads</span>
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
            
            <h2 className="text-lg font-semibold text-gray-800 mb-4 tracking-tight" style={{ fontFamily: "'Marcellus', serif" }}>
              Rendimiento de las campañas
            </h2>
            
            <img 
              src="/assets/images/login-side-image.png" 
              alt="CRM Dashboard Preview"
              className="w-full"
            />
          </div>
        </div>
        
        {/* Full interactive version for larger screens */}
        <div className="show-on-large hidden absolute inset-0 overflow-y-auto">
          <div className="flex flex-col justify-center items-center min-h-screen px-10" style={{ paddingTop: '140px', paddingBottom: '200px' }}>
            <div className="w-full max-w-6xl mx-auto">
              <div className="mb-12">
                <h1 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Marcellus', serif" }}>
                  Potencia tu adquisición<br />de clientes
                </h1>
                <p className="text-gray-600 max-w-md text-lg mb-6">
                  Cada oportunidad comercial, meticulosamente gestionada en un solo lugar.
                </p>
                
                <div className="flex items-center">
                  <button className="text-sm text-[#1a2e35] font-medium flex items-center">
                    <span>Accede para gestionar tus leads</span>
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
              
              {/* Campaign Performance Metrics */}
              <div className="mb-8 w-full">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 tracking-tight" style={{ fontFamily: "'Marcellus', serif" }}>
                  Rendimiento de las campañas
                </h2>
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-blue-700 p-6 text-white shadow-md">
                    <p className="text-xs font-medium mb-2 opacity-90 tracking-wide">Emails enviados</p>
                    <p className="text-2xl font-semibold">2.550</p>
                  </div>
                  <div className="bg-blue-800 p-6 text-white shadow-md">
                    <p className="text-xs font-medium mb-2 opacity-90 tracking-wide">Tasa de respuesta</p>
                    <p className="text-2xl font-semibold">4,98%</p>
                  </div>
                  <div className="bg-blue-900 p-6 text-white shadow-md">
                    <p className="text-xs font-medium mb-2 opacity-90 tracking-wide">Nuevas respuestas</p>
                    <p className="text-2xl font-semibold">127</p>
                  </div>
                  <div className="bg-teal-800 p-6 text-white shadow-md">
                    <p className="text-xs font-medium mb-2 opacity-90 tracking-wide">Nuevas reuniones</p>
                    <p className="text-2xl font-semibold">16</p>
                  </div>
                </div>
              </div>
              
              {/* CRM Lead List Preview */}
              <div className="bg-white shadow-lg border border-gray-100 overflow-hidden w-full">
                <div className="flex items-center px-4 py-3 border-b border-gray-200 hover:bg-gray-50">
                  <svg
                    className="w-4 h-4 text-gray-400 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  <div className="flex items-center text-sm text-gray-700">
                    <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                    <span className="font-medium">abril 2025</span>
                    <span className="text-gray-500 mx-1">·</span>
                    <span className="text-gray-500">21</span>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <div className="overflow-y-auto" style={{ maxHeight: '340px' }}>
                    <table className="w-full min-w-full">
                      <thead className="border-b border-gray-200 sticky top-0 bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Reunión</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado Lead</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notas</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-xs">
                        <tr className="hover:bg-gray-50 h-12 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-800 truncate">Carlos Martínez</td>
                          <td className="px-4 py-3 text-gray-600 truncate">K-tuin</td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">02/04/2025 16:45</td>
                          <td className="px-4 py-3">
                            <div className="bg-green-100 text-green-800 px-2 py-1 text-xs font-medium">
                              Positiva - Seguimiento
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-500 truncate">Interesado en presupuesto para mayo</td>
                        </tr>
                        <tr className="hover:bg-gray-50 h-12 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-800 truncate">Laura García</td>
                          <td className="px-4 py-3 text-gray-600 truncate">Grupo Indukern</td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">05/04/2025 15:15</td>
                          <td className="px-4 py-3">
                            <div className="bg-green-700 text-green-100 px-2 py-1 text-xs font-medium">
                              Positiva - Cerrado
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-500 truncate">Contrato firmado</td>
                        </tr>
                        <tr className="hover:bg-gray-50 h-12 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-800 truncate">Alberto Jiménez</td>
                          <td className="px-4 py-3 text-gray-600 truncate">Meliá</td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">10/04/2025 15:30</td>
                          <td className="px-4 py-3">
                            <div className="bg-green-100 text-green-800 px-2 py-1 text-xs font-medium">
                              Positiva - Seguimiento
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-500 truncate">Pendiente de aprobación interna</td>
                        </tr>
                        <tr className="hover:bg-gray-50 h-12 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-800 truncate">Elena Rodríguez</td>
                          <td className="px-4 py-3 text-gray-600 truncate">Solaria Tech</td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">15/04/2025 11:30</td>
                          <td className="px-4 py-3">
                            <div className="bg-red-200 text-red-800 px-2 py-1 text-xs font-medium">
                              Negativa
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-500 truncate">Sin presupuesto hasta 2026</td>
                        </tr>
                        <tr className="hover:bg-gray-50 h-12 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-800 truncate">Javier López</td>
                          <td className="px-4 py-3 text-gray-600 truncate">Baxi</td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">18/04/2025 15:00</td>
                          <td className="px-4 py-3">
                            <div className="bg-green-700 text-green-100 px-2 py-1 text-xs font-medium">
                              Positiva - Cerrado
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-500 truncate">Acuerdo de colaboración firmado</td>
                        </tr>
                        <tr className="hover:bg-gray-50 h-12 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-800 truncate">Alejandro Torres</td>
                          <td className="px-4 py-3 text-gray-600 truncate">Mercadona</td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">25/04/2025 12:15</td>
                          <td className="px-4 py-3">
                            <div className="bg-blue-200 text-blue-900 px-2 py-1 text-xs font-medium">
                              No se ha presentado
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-500 truncate">Reprogramada para la próxima semana</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div className="p-3 border-t border-gray-100 flex justify-between items-center">
                  <div className="text-xs text-gray-500">Mostrando 6 de 21 leads</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};